import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAvailableMetrics, useMetrics } from '../../../Data/Reports';
import { usePreference } from '../../../Data/Preference';
import { selectableDateRangesQuick, dateRanges, selectableDateRanges } from '../../../Data/DateRanges';
import Modal from '../../../Components/Modal';
import Form, { DateRange, Actions as FormActions } from '../../../Components/Form';
import DateTime from '../../../Components/Formatting/DateTime';
import API from '../../../Services/API';
import { useNewWindow } from '../../../Services/helpers';
import { useToasts } from '../../../Components/Toasts';

const MetricContext = createContext();

export const useMetricContext = () => {
    return useContext(MetricContext);
};

export const MetricsProvider = ({ children, metrics: providedMetricsConfig = null, options: optionsConfig, selectedTags: providedSelectedTags = [] }) => {
    const {availableMetrics} = useAvailableMetrics(false);
    const [dateRange, setDateRange] = usePreference('metrics:dateRange', 'lastThreeMonths');
    const [dateInterval, setDateInterval] = usePreference('metrics:dateInterval', 'auto');
    const [statistic, setStatistic] = usePreference('metrics:statistic', 'count');
    const [invoiceStatistic, setInvoiceStatistic] = usePreference('metrics:invoiceStatistic', 'total');
    const [paymentStatistic, setPaymentStatistic] = usePreference('metrics:paymentStatistic', 'gross');
    const [feeType, setFeeType] = usePreference('metrics:feeType', 'soapy');
    const [planFilter, setPlanFilter] = usePreference('metrics:planFilter', 'active');
    const [selectedTags, setSelectedTags] = useState(providedSelectedTags);
    const [downloadingMetric, setDownloadingMetric] = useState(null);
    const [isCustomDateRangeModalOpen, setIsCustomDateRangeModalOpen] = useState(false);
    const newWindow = useNewWindow();
    const {showError} = useToasts();

    const metricsConfig = useMemo(() => {
        return providedMetricsConfig || availableMetrics?.map(av => ({name: av.name, options: {}})) || [];
    }, [providedMetricsConfig, availableMetrics]);

    const relevantMetricOptions = useMemo(() => {
            if (!availableMetrics || !Array.isArray(availableMetrics)) {
                return [];
            }
            return availableMetrics.reduce((acc, metric) => {
                if (metricsConfig === null || metricsConfig.some(m => m.name === metric.name)) {
                    Object.keys(metric.options).forEach(option => {
                        if (!acc.includes(option)) {
                            acc.push(option);
                        }
                    });
                }
                return acc;
            }, []);
        },
        [availableMetrics, metricsConfig]
    );

    const availableTags = useMemo(() => {
        if (!availableMetrics || !Array.isArray(availableMetrics)) {
            return [];
        }
        const tagSet = new Set();
        availableMetrics.forEach(metric => {
            if (metricsConfig === null || metricsConfig.some(m => m.name === metric.name) && Array.isArray(metric.tags)) {
                metric.tags.forEach(tag => tagSet.add(tag));
            }
        });
        return Array.from(tagSet).sort();
    }, [availableMetrics, metricsConfig]);

    const isCustomDateRange = useMemo(() => {
        return !selectableDateRangesQuick.some(r => r.value === dateRange);
    }, [dateRange]);

    const isPredefinedDateRange = useMemo(() => {
        return selectableDateRanges.some(r => r.value === dateRange);
    }, [dateRange]);

    const customDateRangeTitle = useMemo(() => {
        if (!dateRange) {
            return null;
        }

        if (dateRanges[dateRange] ?? false) {
            return dateRanges[dateRange];
        }

        if (dateRange.includes(':')) {
            const [from, to] = dateRange.split(':');
            return (
                <span className="text-nowrap">
                    <DateTime date={from} hideTime /> &nbsp; - &nbsp; <DateTime date={to} hideTime />
                </span>
            );
        }

        return null;
    }, [dateRange]);

    const menuOptions = useMemo(() => {
        let menuOptions = [];

        if (relevantMetricOptions.includes('statistic')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Statistic'
                },
                {
                    title: 'Count',
                    value: 'count',
                    onClick: () => setStatistic('count'),
                    icon: statistic === 'count' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Cost',
                    value: 'cost',
                    onClick: () => setStatistic('cost'),
                    icon: statistic === 'cost' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Average',
                    value: 'average',
                    onClick: () => setStatistic('average'),
                    icon: statistic === 'average' ? 'CircleCheck' : 'Circle',
                },
            ]);
        }

        if (relevantMetricOptions.includes('invoiceStatistic')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Invoice Statistic'
                },
                {
                    title: 'Count',
                    value: 'count',
                    onClick: () => setInvoiceStatistic('count'),
                    icon: invoiceStatistic === 'count' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Total',
                    value: 'total',
                    onClick: () => setInvoiceStatistic('total'),
                    icon: invoiceStatistic === 'total' ? 'CircleCheck' : 'Circle',
                },
                // {
                //     title: 'Paid',
                //     value: 'paid',
                //     onClick: () => setInvoiceStatistic('paid'),
                //     icon: invoiceStatistic === 'paid' ? 'CircleCheck' : 'Circle',
                // },
                // {
                //     title: 'Outstanding',
                //     value: 'outstanding',
                //     onClick: () => setInvoiceStatistic('outstanding'),
                //     icon: invoiceStatistic === 'outstanding' ? 'CircleCheck' : 'Circle',
                // },
                // {
                //     title: 'Average',
                //     value: 'average',
                //     onClick: () => setInvoiceStatistic('average'),
                //     icon: invoiceStatistic === 'average' ? 'CircleCheck' : 'Circle',
                // },
            ]);
        }

        if (relevantMetricOptions.includes('paymentStatistic')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Payment Statistic'
                },
                {
                    title: 'Count',
                    value: 'count',
                    onClick: () => setPaymentStatistic('count'),
                    icon: paymentStatistic === 'count' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Gross',
                    value: 'gross',
                    onClick: () => setPaymentStatistic('gross'),
                    icon: paymentStatistic === 'gross' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Net',
                    value: 'net',
                    onClick: () => setPaymentStatistic('net'),
                    icon: paymentStatistic === 'net' ? 'CircleCheck' : 'Circle',
                },
            ]);
        }

        if (relevantMetricOptions.includes('dateInterval')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Date Interval'
                },
                {
                    title: 'Auto',
                    value: 'auto',
                    onClick: () => setDateInterval('auto'),
                    icon: dateInterval === 'auto' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Daily',
                    value: 'Date',
                    onClick: () => setDateInterval('Date'),
                    icon: dateInterval === 'Date' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Weekly',
                    value: 'Week',
                    onClick: () => setDateInterval('Week'),
                    icon: dateInterval === 'Week' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Monthly',
                    value: 'Month',
                    onClick: () => setDateInterval('Month'),
                    icon: dateInterval === 'Month' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Yearly',
                    value: 'Year',
                    onClick: () => setDateInterval('Year'),
                    icon: dateInterval === 'Year' ? 'CircleCheck' : 'Circle',
                },
            ]);
        }

        if (relevantMetricOptions.includes('feeType')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Fee Type'
                },
                {
                    title: 'Soapy Fees',
                    value: 'soapy',
                    onClick: () => setFeeType('soapy'),
                    icon: feeType === 'soapy' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Stripe Fees',
                    value: 'stripe',
                    onClick: () => setFeeType('stripe'),
                    icon: feeType === 'stripe' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Total Fees',
                    value: 'total',
                    onClick: () => setFeeType('total'),
                    icon: feeType === 'total' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Fee Rate',
                    value: 'rate',
                    onClick: () => setFeeType('rate'),
                    icon: feeType === 'rate' ? 'CircleCheck' : 'Circle',
                },
            ]);
        }

        if (relevantMetricOptions.includes('planFilter')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Plan Filter'
                },
                {
                    title: 'All Plans',
                    value: 'all',
                    onClick: () => setPlanFilter('all'),
                    icon: planFilter === 'all' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Active Plans',
                    value: 'active',
                    onClick: () => setPlanFilter('active'),
                    icon: planFilter === 'active' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Paid Plans',
                    value: 'paid',
                    onClick: () => setPlanFilter('paid'),
                    icon: planFilter === 'paid' ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Legacy Plans',
                    value: 'legacy',
                    onClick: () => setPlanFilter('legacy'),
                    icon: planFilter === 'legacy' ? 'CircleCheck' : 'Circle',
                },
            ]);
        }

        if (relevantMetricOptions.includes('dateRange')) {
            menuOptions = menuOptions.concat([
                {
                    section: 'Date Range'
                },
                ...selectableDateRangesQuick.map(r => ({
                    title: r.label,
                    value: r.value,
                    onClick: () => setDateRange(r.value),
                    icon: r.value === dateRange ? 'CircleCheck' : 'Circle',
                })),
                {
                    title: isCustomDateRange ? customDateRangeTitle : 'Other',
                    value: 'custom',
                    onClick: () => setIsCustomDateRangeModalOpen(true),
                    icon: isCustomDateRange ? 'Pencil' : 'Circle',
                }
            ]);
        }

        return menuOptions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relevantMetricOptions, dateRange, dateInterval, statistic, invoiceStatistic, paymentStatistic, feeType, planFilter, isCustomDateRange, isPredefinedDateRange, customDateRangeTitle]);

    const config = useMemo(
        () => ({
            metrics: metricsConfig,
            options: Object.assign(
                {dateRange, dateInterval, statistic, invoiceStatistic, paymentStatistic, feeType, planFilter},
                optionsConfig ?? {}
            )
        }),
        [metricsConfig, dateRange, dateInterval, statistic, invoiceStatistic, paymentStatistic, feeType, planFilter, optionsConfig]
    );

    const {metrics} = useMetrics(true, config);

    const downloadMetric = (metric) => {
        setDownloadingMetric(metric.name);
        API.post('/reports/metrics/download', {
            metric: metric.name,
            options: metric.options,
        }).then(res => {
            newWindow(res.downloadUrl);
        }).catch(
            error => showError(API.parseErrorResponse(error) ?? 'Error downloading metric')
        ).finally(() => setDownloadingMetric(null));
    };

    const filteredMetrics = useMemo(() => {
        if (!metrics || !Array.isArray(metrics)) {
            return metrics;
        }
        if (selectedTags.length === 0) {
            return metrics;
        }
        return metrics.filter(metric => {
            const metricData = availableMetrics?.find(m => m.name === metric.name);
            if (!metricData || !Array.isArray(metricData.tags)) {
                return false;
            }
            return metricData.tags.some(tag => selectedTags.includes(tag));
        });
    }, [metrics, selectedTags, availableMetrics]);

    const providerValues = {
        metrics: filteredMetrics,
        availableTags,
        selectedTags,
        setSelectedTags,
        dateRange,
        setDateRange,
        dateInterval,
        setDateInterval,
        statistic,
        setStatistic,
        invoiceStatistic,
        setInvoiceStatistic,
        paymentStatistic,
        setPaymentStatistic,
        feeType,
        setFeeType,
        planFilter,
        setPlanFilter,
        menuOptions,
        downloadMetric,
        downloadingMetric,
    };

    const handleCustomDateRangeSubmit = (formData) => {
        if (formData.dateRange) {
            setDateRange(formData.dateRange);
        }
        setIsCustomDateRangeModalOpen(false);
    };

    return (
        <MetricContext.Provider value={providerValues}>
            {children}
            <Modal
                isOpen={isCustomDateRangeModalOpen}
                setIsOpen={setIsCustomDateRangeModalOpen}
                title="Custom Date Range"
                dismissible
            >
                <Form
                    onSubmit={handleCustomDateRangeSubmit}
                    data={{ dateRange }}
                    closeModalOnSubmit
                >
                    <DateRange name="dateRange" />
                    <FormActions submit={{ label: 'Apply' }} cancel />
                </Form>
            </Modal>
        </MetricContext.Provider>
    );
};
